import React, { useEffect, useState, useRef } from "react";
import { useTransactions } from "./useTransactions";
import "../css/FrameStyles.css";
import loadingAnimation from "../assets/Loading.json";
import Lottie from "lottie-react";
import SummaryCard from "./SummaryCard";
import CapitalSummaryCard from "./CapitalSummaryCard";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import cloneDeep from "lodash/cloneDeep";
import { executeTaxHarvest } from '../api/transactions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye } from '@fortawesome/free-solid-svg-icons';
import DashboardUnlock from './DashboardUnlock';
import { fetchTaxRates } from '../utils/taxRatesCache';

function TaxLossOpportunitiesFrame({ username, isUnlocked = true, onUnlockSuccess, hasFreeAccess = false, userTier = 'STANDARD' }) {
  const isMobileView = window.innerWidth <= 768;
  const tableRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [transactionTypes] = useState([
    { name: "SELL", value: "SELL" },
    { name: "DIVIDEND", value: "DIVIDEND" },
    { name: "OPTION", value: "OPTION" },
  ]);
  const [taxTypes] = useState([
    { name: "Long Term (15%)", value: "Long Term(15%)" },
    { name: "Short Term (30%)", value: "Short Term(30%)" },
    { name: "Dividend (30%)", value: "Dividend(30%)" },
  ]);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.IN },
    taxType: { value: null, matchMode: FilterMatchMode.IN },
  });

  const toNumeric = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === "string") {
      const numeric = Number(value.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(numeric) ? numeric : 0;
    }
    return 0;
  };

  const formatCurrencyValue = (amount) => {
    const numeric = toNumeric(amount);
    const formatted = Math.abs(numeric).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return numeric < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const getRemainingQuantity = (rowData) => {
    const originalQty = toNumeric(rowData?.quantity);
    const settledQty = toNumeric(rowData?.settledQuantity);
    const remaining = originalQty - settledQty;
    return Number.isFinite(remaining) ? Math.max(remaining, 0) : 0;
  };

  const formatQuantityDisplay = (qty) => {
    if (!Number.isFinite(qty)) {
      return "0";
    }
    const rounded = Number(qty.toFixed(2));
    if (Number.isInteger(rounded)) {
      return rounded.toString();
    }
    return rounded.toFixed(2).replace(/\.?0+$/, "");
  };

  const formatPriceDisplay = (price) => {
    if (typeof price === "string" && price.trim() !== "") {
      return price;
    }
    return formatCurrencyValue(price || 0);
  };

  const renderCurrentPriceCell = (rowData) => {
    const remainingQty = getRemainingQuantity(rowData);
    const quantityLabel = formatQuantityDisplay(remainingQty);
    const priceLabel = formatPriceDisplay(rowData.currentPrice);

    return (
      <span className="table-value-with-annotation">
        {priceLabel}
        <span className="table-annotation">
          (
          <span
            className="table-tooltip-trigger remaining-quantity-tooltip"
            data-pr-tooltip={`Remaining quantity: ${quantityLabel}`}
            title={`Remaining quantity: ${quantityLabel}`}
          >
            {quantityLabel}
          </span>
          )
        </span>
      </span>
    );
  };

  const renderTickerCell = (rowData) => {
    const ticker = (rowData.tickerSymbol || rowData.ticker || "--").toString().trim();
    return <span className="table-highlight-text table-highlight-text--ticker">{ticker || "--"}</span>;
  };

  const renderTaxSavingsCell = (rowData) => {
    const savingsValue = toNumeric(
      rowData.taxesOwedValue ?? rowData.taxesOwed
    );
    const displayValue = formatCurrencyValue(savingsValue);

    const rawTaxType = rowData.taxType || "";
    const descriptorMatch = rawTaxType.match(/^[A-Za-z\s]+/);
    const descriptor = descriptorMatch ? descriptorMatch[0].trim() : rawTaxType;
    const abbreviation = descriptor ? descriptor.charAt(0).toUpperCase() : "–";

    const rateMatch = rawTaxType.match(/(\d+(\.\d+)?)%/);
    const rateLabel = rateMatch ? `${rateMatch[1]}%` : "";
    const tooltipText = descriptor
      ? `${descriptor}${rateLabel ? ` at ${rateLabel} tax rate` : ""}`
      : "Tax type unavailable";

    return (
      <span className="table-highlight-text table-highlight-text--value">
        <span className="table-value-with-annotation">
          {displayValue}
          <span className="table-annotation">
            (
            <span
              className="table-tooltip-trigger tax-type-tooltip"
              data-pr-tooltip={tooltipText}
              title={tooltipText}
            >
              {abbreviation}
            </span>
            )
          </span>
        </span>
      </span>
    );
  };
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [expandedRows, setExpandedRows] = useState(null);
  
  // Unlock modal state
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  
  // Execution state
  const [selectedLossOpportunities, setSelectedLossOpportunities] = useState([]);
  const [showExecuteConfirm, setShowExecuteConfirm] = useState(false);
  const [replacementSelections, setReplacementSelections] = useState({}); // Map of lotId -> { buyReplacement: boolean, replacementTicker: string }

  // Fetch data hooks
  const {
    data: gainData,
    loading: gainLoading,
    error: gainError,
  } = useTransactions("transactions/getCurrentYearTaxableProfit");
  const {
    data: lossData,
    loading: lossLoading,
    error: lossError,
  } = useTransactions("transactions/getCurrentYearTaxableLoss");
  const {
    data: washSaleData,
    loading: washSaleLossLoading,
    error: washSaleLossError,
  } = useTransactions("transactions/getPastWashSaleTransactions");
  const { data, loading, error } = useTransactions(
    "transactions/getPotentialLossOpportunities"
  );

  // Essential state for tax calculations and UI
  const [netShortTerm, setNetShortTerm] = useState(0);
  const [netLongTerm, setNetLongTerm] = useState(0);
  const [potentialUnrealizedLosses, setPotentialUnrealizedLosses] = useState(0);
  const [capitalGainsOffset, setCapitalGainsOffset] = useState(0);
  const [incomeDeduction, setIncomeDeduction] = useState(0);
  const [totalTaxSavings, setTotalTaxSavings] = useState(0);
  
  // Tax rates
  const [longTermTaxRate, setLongTermTaxRate] = useState(20);
  const [shortTermTaxRate, setShortTermTaxRate] = useState(30);

  // Add new state for netGains and currentTaxesOwed
  const [netGains, setNetGains] = useState(0);
  const [currentTaxesOwed, setCurrentTaxesOwed] = useState(0);
  // Bump when executedTaxLossHarvesting in sessionStorage changes (e.g. execute from chat) so net gains effect re-runs
  const [executedStorageRevision, setExecutedStorageRevision] = useState(0);

  // Fetch tax rates from backend (using cached utility)
  const getTaxRates = async () => {
    if (!username) return;
    const rates = await fetchTaxRates(username);
    if (rates) {
      setLongTermTaxRate(rates.LONG_TERM);
      setShortTermTaxRate(rates.SHORT_TERM);
    }
  };

  // Fetch tax rates on component mount
  useEffect(() => {
    if (username) {
      getTaxRates();
    }
  }, [username]);

  const calculateTaxSavings = (
    shortTermGains,
    shortTermLosses,
    longTermGains,
    longTermLosses,
    washSaleLosses,       // disallowed losses added back into short-term
    potentialShortTermLoss,
    potentialLongTermLoss,
    shortTermTaxRate,
    longTermTaxRate
  ) => {
    const stRate = shortTermTaxRate / 100;
    const ltRate = longTermTaxRate / 100;

    // Step 1: Adjust short-term losses to include disallowed wash sales
    const adjustedShortTermLosses = shortTermLosses + washSaleLosses;
    const adjustedLongTermLosses = longTermLosses;

    // Step 2: Net positions
    let netST = shortTermGains - adjustedShortTermLosses;
    let netLT = longTermGains - adjustedLongTermLosses;

    // Step 3: Offsets
    let stOffset = Math.min(shortTermGains, adjustedShortTermLosses);
    let ltOffset = Math.min(longTermGains, adjustedLongTermLosses);

    let crossOffsetST = 0;
    let crossOffsetLT = 0;

    if (netST < 0 && netLT > 0) {
      const offset = Math.min(Math.abs(netST), netLT);
      crossOffsetLT = offset;
      netST += offset;
      netLT -= offset;
    } else if (netLT < 0 && netST > 0) {
      const offset = Math.min(Math.abs(netLT), netST);
      crossOffsetST = offset;
      netLT += offset;
      netST -= offset;
    }

    const totalOffsetLossUsed = stOffset + ltOffset + crossOffsetST + crossOffsetLT;

    // Step 4: Cap savings to real net gains
    const netGain = Math.max(0, shortTermGains - adjustedShortTermLosses + longTermGains - adjustedLongTermLosses);
    const cappedLossOffset = Math.min(totalOffsetLossUsed, netGain);

    // Step 5: Weighted rate application
    const stWeighted = (stOffset + crossOffsetST) / totalOffsetLossUsed || 0;
    const ltWeighted = (ltOffset + crossOffsetLT) / totalOffsetLossUsed || 0;

    const totalUnrealizedLoss = potentialShortTermLoss + potentialLongTermLoss;
    const capitalGainsSavings = Math.min(netGain, totalUnrealizedLoss);

    // Step 6: Calculate actual net gains (can be negative)
    const actualNetGains = shortTermGains - adjustedShortTermLosses + longTermGains - adjustedLongTermLosses;
    
    // Step 7: If net gains are negative, add to income deduction
    // Remaining losses → income deduction
    const remainingLosses = totalUnrealizedLoss - capitalGainsSavings;
    // If net gains are negative, add absolute value to income deduction
    const negativeNetGains = actualNetGains < 0 ? Math.abs(actualNetGains) : 0;
    const incomeDeductionWithoutNetGains = Math.min(remainingLosses, 3000);
    const incomeDeduction = Math.min(remainingLosses + negativeNetGains, 3000);

    // Step 8: Total savings
    const totalSavings = (capitalGainsSavings + incomeDeductionWithoutNetGains) * shortTermTaxRate / 100 ;

    return {
      capitalGainsOffset: capitalGainsSavings,
      incomeDeduction,
      totalSavings,
      netShortTerm: netST,
      netLongTerm: netLT
    };
  };

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    
    // Only proceed if all data is available and not loading
    if (gainData && lossData && data && !gainLoading && !lossLoading && !loading && !washSaleLossLoading) {
      // Get executed tax loss harvesting transactions (for demo accounts)
      const executedTaxLossHarvesting = username === "demo"
        ? JSON.parse(sessionStorage.getItem('executedTaxLossHarvesting') || '[]')
        : [];
      
      let shortTermGains = 0;
      let longTermGains = 0;
      let shortTermLosses = 0;
      let longTermLosses = 0;
      let washSaleLosses = 0;
      let potentialShortTermLoss = 0;
      let potentialLongTermLoss = 0;
      
      // Track executed loss amounts by tax type
      let executedShortTermLoss = 0;
      let executedLongTermLoss = 0;
      const executedLossIds = new Set(executedTaxLossHarvesting.map(tx => tx.id));

      // Process gains
      gainData?.forEach((transaction) => {
        const transactionYear = new Date(transaction.date).getFullYear();
        if (transactionYear !== currentYear) return;

        const amount = Math.abs(transaction.profitLoss);
        if (transaction.capitalGainTaxType === "SHORT" || transaction.capitalGainTaxType === undefined  || transaction.capitalGainTaxType === null) shortTermGains += amount;
        if (transaction.capitalGainTaxType === "LONG") longTermGains += amount;
      });

      // Process losses
      lossData?.forEach((transaction) => {
        const transactionYear = new Date(transaction.date).getFullYear();
        if (transactionYear !== currentYear) return;

        const amount = Math.abs(transaction.profitLoss);
        if (transaction.capitalGainTaxType === "SHORT" || transaction.capitalGainTaxType === undefined || transaction.capitalGainTaxType === null) shortTermLosses += amount;
        if (transaction.capitalGainTaxType === "LONG") longTermLosses += amount;
      });

      // Process potential losses, excluding executed ones
      data?.forEach((transaction) => {
        // Skip if this transaction was executed
        if (executedLossIds.has(transaction.id)) {
          return;
        }
        
        const transactionYear = new Date(transaction.date).getFullYear();
        const amount = Math.abs(transaction.unrealizedProfitLoss);
        const transactionDate = new Date(transaction.date);
        const currentDate = new Date();
        const differenceInDays = (currentDate - transactionDate) / (1000 * 60 * 60 * 24);

        if (differenceInDays <= 365) potentialShortTermLoss += amount;
        if (differenceInDays > 365) potentialLongTermLoss += amount;
      });
      
      // Add executed losses to realized losses (they become realized when executed)
      executedTaxLossHarvesting.forEach(executed => {
        const lossAmount = Math.abs(executed.unrealizedProfitLoss || executed.profitLoss || 0);
        // Determine if short-term or long-term based on capitalGainTaxType or purchase date
        if (executed.capitalGainTaxType === 'LONG') {
          executedLongTermLoss += lossAmount;
          longTermLosses += lossAmount; // Add to realized long-term losses
        } else {
          // Default to short-term (SHORT or undefined)
          executedShortTermLoss += lossAmount;
          shortTermLosses += lossAmount; // Add to realized short-term losses
        }
      });

      // Process wash sales
      washSaleData?.forEach((transaction) => {
        const transactionYear = new Date(transaction.date).getFullYear();
        if (transactionYear !== currentYear) return;
        
        if (transaction.capitalGainTaxType === "SHORT" || transaction.capitalGainTaxType === undefined  || transaction.capitalGainTaxType === null) {
          washSaleLosses += Math.abs(transaction.profitLoss);
        }
      });

      // Calculate tax savings
      const savings = calculateTaxSavings(
        shortTermGains,
        shortTermLosses,
        longTermGains,
        longTermLosses,
        washSaleLosses,
        potentialShortTermLoss,
        potentialLongTermLoss,
        shortTermTaxRate,
        longTermTaxRate
      );

      // Update state with calculation results
      setNetShortTerm(savings.netShortTerm);
      setNetLongTerm(savings.netLongTerm);
      setCapitalGainsOffset(savings.capitalGainsOffset);
      setIncomeDeduction(savings.incomeDeduction);
      setTotalTaxSavings(savings.totalSavings);
      
      // Calculate actual net gains BEFORE cross-offsetting for display
      // This shows the true net position: (gains - losses) across all categories
      const actualNetGains = shortTermGains - (shortTermLosses + washSaleLosses) + longTermGains - longTermLosses;
      setNetGains(actualNetGains);
      
      // Update potential unrealized losses (excluding executed ones)
      setPotentialUnrealizedLosses(potentialShortTermLoss + potentialLongTermLoss);
      
      // If net gains are negative, taxes owed should be 0
      // Otherwise, calculate taxes on positive net gains (after cross-offsetting)
      const calculatedTaxes = (savings.netShortTerm * shortTermTaxRate / 100) +
        (savings.netLongTerm * longTermTaxRate / 100);
      setCurrentTaxesOwed(actualNetGains < 0 ? 0 : Math.max(0, calculatedTaxes));
    }
  }, [gainData, lossData, washSaleData, data, gainLoading, lossLoading, loading, washSaleLossLoading, shortTermTaxRate, longTermTaxRate, username, tableData, executedStorageRevision]);

  // Calculate potential unrealized losses from table data
  const getSummaryCardsValue = (data = tableData) => {
    // Get executed transactions to exclude them
    const executedTaxLossHarvesting = username === "demo"
      ? JSON.parse(sessionStorage.getItem('executedTaxLossHarvesting') || '[]')
      : [];
    const executedIds = new Set(executedTaxLossHarvesting.map(tx => tx.id));
    
    const total = data
      .filter(transaction => !executedIds.has(transaction.id))
      .reduce((acc, transaction) => {
        return acc + Math.abs(transaction.profitLossWithoutPrefix || 0);
      }, 0);
    
    setPotentialUnrealizedLosses(total.toFixed(2));
  };

  useEffect(() => {
    const fetchAndProcessData = async () => {
        if (username) {
            await getTaxRates(); // Fetch tax rates before processing data
        }

        if (!loading && error === null) {
            // Get executed transactions from sessionStorage to filter them out
            const executedTaxLossHarvesting = username === "demo"
              ? JSON.parse(sessionStorage.getItem('executedTaxLossHarvesting') || '[]')
              : [];
            const executedIds = new Set(executedTaxLossHarvesting.map(tx => tx.id));
            
            // Filter out executed transactions from the data
            let tempTableData = cloneDeep(data).filter(item => !executedIds.has(item.id));

            const currentYear = new Date().getFullYear().toString();
            let totalGain = 0;
            let totalLoss = 0;

            // Utility to format numbers in a string to 2 decimals
            function formatNumbersInString(str) {
              if (!str) return str;
              return str.replace(/(\d+\.\d{2})\d*/g, (match) => Number(match).toFixed(2));
            }

            const processedData = tempTableData.map((item) => {
                const tempItem = cloneDeep(item);

                const currentDate = new Date();
                const transactionDate = new Date(tempItem.date);
                const differenceInDays =
                    (currentDate - transactionDate) / (1000 * 60 * 60 * 24);

                tempItem.purchaseDate =
                    item.settlementTransactions.length > 1
                        ? "Multiple"
                        : item.settlementTransactions.length === 1
                        ? item.settlementTransactions[0].date
                        : item.date;

                const purchasePriceValue = item.settlementTransactions.length > 0
                        ? (
                              item.settlementTransactions.reduce(
                                  (acc, transaction) => acc + transaction.price * transaction.settledQuantityForCurrentTransaction,
                                  0
                              ) /
                              item.settlementTransactions.reduce(
                                  (acc, transaction) => acc + transaction.settledQuantityForCurrentTransaction,
                                  0
                              )
                      )
                    : 0;
                tempItem.purchasePriceValue = purchasePriceValue;
                tempItem.purchasePrice = formatCurrencyValue(purchasePriceValue);
                tempItem.priceValue = toNumeric(item.price);

                tempItem.taxType =
                    differenceInDays > 365
                        ? formatNumbersInString(`Long Term (${Number(longTermTaxRate).toFixed(2)}%)`)
                        : formatNumbersInString(`Short Term (${Number(shortTermTaxRate).toFixed(2)}%)`);

                const taxRate = differenceInDays > 365 ? longTermTaxRate : shortTermTaxRate;
                const taxesOwedValue = item.unrealizedProfitLoss * (taxRate / 100);
                tempItem.taxesOwedValue = taxesOwedValue;
                tempItem.taxesOwed = formatCurrencyValue(taxesOwedValue);

                const remainingQuantityValue = item.quantity - item.settledQuantity;
                tempItem.unsettledQuantity = remainingQuantityValue.toFixed(2);
                tempItem.price = formatCurrencyValue(item.price);
                const currentPriceValue =
                  item.currentPrice !== undefined ? Number(item.currentPrice) : 0;
                tempItem.currentPriceValue = currentPriceValue;
                tempItem.currentPrice = formatCurrencyValue(currentPriceValue);
                const unrealizedProfitLossValue = typeof item.unrealizedProfitLoss === 'number' 
                  ? item.unrealizedProfitLoss 
                  : (typeof item.unrealizedProfitLoss === 'string' 
                    ? parseFloat(item.unrealizedProfitLoss.replace(/[$,]/g, '')) || 0
                    : Number(item.unrealizedProfitLoss) || 0);
                tempItem.profitLossWithoutPrefix = unrealizedProfitLossValue;
                tempItem.unrealizedProfitLoss = formatCurrencyValue(unrealizedProfitLossValue);

                // Format description numbers
                tempItem.description = formatNumbersInString(item.description);

                return tempItem;
            });
            setTableData(processedData);
            getSummaryCardsValue(processedData); // Update summary values
        }
    };

    fetchAndProcessData(); // Execute the function
}, [username, data, longTermTaxRate, shortTermTaxRate, loading, error]);

  // Listen for tax loss harvesting execution events to refresh table data and net gains
  useEffect(() => {
    if (username === "demo") {
      const handleTaxLossHarvestingExecuted = () => {
        // Re-filter tableData to exclude newly executed transactions
        const executedTaxLossHarvesting = JSON.parse(sessionStorage.getItem('executedTaxLossHarvesting') || '[]');
        const executedIds = new Set(executedTaxLossHarvesting.map(tx => tx.id));
        
        // Filter out executed transactions from current tableData
        setTableData(prevData => prevData.filter(item => !executedIds.has(item.id)));
        // Force net gains effect to re-run so it re-reads sessionStorage and updates net gains
        setExecutedStorageRevision(prev => prev + 1);
      };
      
      window.addEventListener('taxLossHarvestingExecuted', handleTaxLossHarvestingExecuted);
      return () => {
        window.removeEventListener('taxLossHarvestingExecuted', handleTaxLossHarvestingExecuted);
      };
    }
  }, [username]);

  // Show loading if any data is still loading
  // Only show loading spinner if we're actively fetching data
  if (loading || gainLoading || lossLoading || washSaleLossLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px"
        }}
      >
        <Lottie
          animationData={loadingAnimation}
          style={{ height: "150px", width: "150px" }} // Adjust loader size
        />
      </div>
    );
  }

  if (gainError || lossError || error) {
    return (
      <div>
        Error: {gainError || lossError || error}, please contact support for
        help: support@profitree-tax.com
      </div>
    );
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    initFilters();
  };

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.IN },
      taxType: { value: null, matchMode: FilterMatchMode.IN },
    });
    setGlobalFilterValue("");
    if (tableRef.current) {
      tableRef.current.reset();
    }
  };

  // Utility function to format top 3 recommendations
  const formatTop3Recommendations = (recommendations) => {
    if (!recommendations || recommendations.length === 0) {
      return <span className="table-highlight-text table-highlight-text--muted">No recommendations</span>;
    }

    let peerList =
      typeof recommendations === "string"
        ? recommendations.split(/[\s,]+/).filter(Boolean)
        : recommendations;

    const cleanedPeers = peerList
      .map((peer) => peer.trim())
      .filter(Boolean)
      .slice(0, 3);

    if (cleanedPeers.length === 0) {
      return <span className="table-highlight-text table-highlight-text--muted">No recommendations</span>;
    }

    return (
      <span className="table-highlight-text table-highlight-text--alt">
        {cleanedPeers.join(", ")}
      </span>
    );
  };

  // Unlock handlers
  const handleUnlockClick = () => {
    setShowUnlockModal(true);
  };

  const handleUnlockSuccess = (paymentIntent, unlockFee, savings) => {
    console.log('TaxLossOpportunitiesFrame unlocked:', { paymentIntent, unlockFee, savings });
    setShowUnlockModal(false);
    
    // Call parent component's success handler
    if (onUnlockSuccess) {
      onUnlockSuccess(paymentIntent, unlockFee, savings);
    }
  };

  const handleExecuteSelectedLots = () => {
    if (selectedLossOpportunities.length === 0) {
      toast.warning("Please select at least one lot to execute.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    // Initialize replacement selections
    const initialSelections = {};
    selectedLossOpportunities.forEach(lot => {
      const recommendations = lot.peerRecommendations;
      const firstRecommendation = recommendations && (
        Array.isArray(recommendations) ? recommendations[0] : 
        typeof recommendations === 'string' ? recommendations.split(/[\s,]+/)[0] : ''
      );
      initialSelections[lot.id] = {
        buyReplacement: false,
        replacementTicker: firstRecommendation || ''
      };
    });
    setReplacementSelections(initialSelections);
    setShowExecuteConfirm(true);
  };

  const confirmExecute = async () => {
    if (username !== "demo") {
      setShowExecuteConfirm(false);
      return;
    }

    try {
      const executedCount = selectedLossOpportunities.length;
      const lotsWithReplacements = selectedLossOpportunities.filter(lot => 
        replacementSelections[lot.id]?.buyReplacement && replacementSelections[lot.id]?.replacementTicker
      );
      
      // Remove executed lots from tableData (positions)
      // Note: The useEffect will also filter these out when data refreshes, but we update immediately for UI responsiveness
      const executedLotIds = selectedLossOpportunities.map(lot => lot.id);
      setTableData(prevData => prevData.filter(item => !executedLotIds.includes(item.id)));
      
      // For demo: Store executed transactions and new buy positions in session storage
      // This allows other calculators to see the changes
      // Store with matching keys that can be used to identify positions across calculators
      const executedTransactions = selectedLossOpportunities.map(lot => {
        const lossAmount = Math.abs(lot.profitLossWithoutPrefix || lot.unrealizedProfitLoss || 0);
        // Determine tax type from lot's taxType field
        const isLongTerm = lot.taxType?.includes('Long') || lot.taxType?.includes('LONG');
        const capitalGainTaxType = isLongTerm ? 'LONG' : 'SHORT';
        
        return {
          id: lot.id,
          ticker: lot.tickerSymbol,
          quantity: lot.quantity,
          costBasis: lot.priceValue || lot.purchasePriceValue || 0,
          date: lot.date || lot.purchaseDate,
          account: lot.accountName,
          type: 'SELL',
          executed: true,
          unrealizedProfitLoss: -lossAmount, // Store as negative (loss)
          profitLoss: -lossAmount, // Also store as profitLoss for realized loss calculation
          capitalGainTaxType: capitalGainTaxType, // Store tax type for proper categorization
          // Store matching keys for cross-calculator matching
          matchingKey: `${lot.tickerSymbol}-${lot.quantity}-${lot.priceValue || lot.purchasePriceValue || 0}-${lot.date || lot.purchaseDate}`
        };
      });
      
      // Helper function to get current price for a ticker
      const getReplacementPrice = (ticker) => {
        // Try to get price from an existing position with this ticker
        const existingPosition = tableData.find(pos => pos.tickerSymbol === ticker);
        if (existingPosition && existingPosition.currentPriceValue) {
          return existingPosition.currentPriceValue;
        }
        
        // Mock prices for common tickers (for demo purposes)
        const mockPrices = {
          'AAPL': 180,
          'MSFT': 420,
          'GOOGL': 140,
          'AMZN': 150,
          'TSLA': 250,
          'META': 500,
          'NVDA': 800,
          'VOO': 410,
          'SPY': 450,
          'QQQ': 380,
          'BRK.B': 350,
          'JNJ': 160,
          'V': 250,
          'PG': 150,
          'UNH': 500,
          'MA': 400,
          'HD': 350,
          'DIS': 100,
          'BAC': 35,
          'WMT': 160
        };
        return mockPrices[ticker.toUpperCase()] || 100; // Default to $100 if not found
      };
      
      const newBuyPositions = lotsWithReplacements.map((lot, index) => {
        const replacement = replacementSelections[lot.id];
        const ticker = replacement.replacementTicker;
        
        // Calculate total dollar amount from sold position
        const soldQuantity = lot.quantity;
        const soldPrice = Math.abs(lot.currentPriceValue || 0);
        const totalAmount = soldQuantity * soldPrice;
        
        // Get current price for replacement ticker
        const replacementPrice = getReplacementPrice(ticker);
        
        // Calculate quantity based on total amount and replacement price
        const buyQuantity = totalAmount / replacementPrice;
        const formattedQuantity = Math.round(buyQuantity * 100) / 100; // Round to 2 decimals
        const formattedPrice = Math.abs(replacementPrice).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        
        return {
          id: `buy-${Date.now()}-${index}-${Math.random()}`,
          ticker: ticker,
          quantity: formattedQuantity,
          costBasis: replacementPrice, // Buy at replacement stock's current price
          date: new Date().toISOString().slice(0, 10),
          account: lot.accountName,
          type: 'BUY',
          executed: true,
          purchaseDate: new Date().toISOString().slice(0, 10),
          currentPrice: replacementPrice,
          unrealizedProfitLoss: 0, // New position, no gain/loss yet
          description: `Buy ${formattedQuantity.toFixed(2)} shares of ${ticker} at $${formattedPrice}`,
          totalAmount: totalAmount // Store original amount for reference
        };
      });
      
      // Prepare transactions for backend API call
      const transactionsToLog = [
        // Add SELL transactions for executed lots
        ...executedTransactions.map(tx => ({
          id: tx.id,
          type: 'SELL',
          date: tx.date,
          tickerSymbol: tx.ticker,
          quantity: tx.quantity,
          price: tx.costBasis,
          currentPrice: tx.costBasis, // For tax loss harvesting, current price is same as cost basis
          totalAmount: tx.quantity * tx.costBasis,
          costBasis: tx.costBasis,
          profitLoss: tx.profitLoss || -Math.abs(tx.unrealizedProfitLoss || 0),
          accountName: tx.account,
          isEquity: true
        })),
        // Add BUY transactions for replacement securities
        ...newBuyPositions.map(buy => ({
          id: buy.id,
          type: 'BUY',
          date: buy.date,
          tickerSymbol: buy.ticker,
          quantity: buy.quantity,
          price: buy.costBasis,
          currentPrice: buy.currentPrice,
          totalAmount: buy.quantity * buy.costBasis,
          costBasis: buy.costBasis,
          profitLoss: 0, // New position, no gain/loss yet
          accountName: buy.account,
          isEquity: true
        }))
      ];
      
      // Call backend API to log trade execution
      try {
        const result = await executeTaxHarvest(transactionsToLog);

        if (result && result.status === 'success') {
          console.log('Trade execution logged:', result.message);
        } else {
          console.warn('Trade execution logging returned unexpected response:', result);
        }
      } catch (apiError) {
        console.error('Error logging trade execution to backend:', apiError);
        // Don't block execution if logging fails - still proceed with local updates
      }
      
      // Store in sessionStorage for cross-calculator visibility
      const existingExecuted = JSON.parse(sessionStorage.getItem('executedTaxLossHarvesting') || '[]');
      const existingBuys = JSON.parse(sessionStorage.getItem('newBuyPositions') || '[]');
      
      sessionStorage.setItem('executedTaxLossHarvesting', JSON.stringify([...existingExecuted, ...executedTransactions]));
      sessionStorage.setItem('newBuyPositions', JSON.stringify([...existingBuys, ...newBuyPositions]));
      
      // Trigger a custom event to notify other components
      window.dispatchEvent(new CustomEvent('taxLossHarvestingExecuted', {
        detail: { executedTransactions, newBuyPositions }
      }));
      
      // Clear selections and dialog
      setSelectedLossOpportunities([]);
      setShowExecuteConfirm(false);
      setReplacementSelections({});
      
      // Show success toast
      const replacementMsg = lotsWithReplacements.length > 0 
        ? ` ${lotsWithReplacements.length} replacement${lotsWithReplacements.length > 1 ? 's' : ''} added.`
        : '';
      toast.success(`Successfully executed ${executedCount} tax lot${executedCount > 1 ? 's' : ''}.${replacementMsg}`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } catch (error) {
      console.error("Error executing lots:", error);
      toast.error("Error executing lots. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const renderHeader = () => {
    return (
      <div className="datatable-header-wrapper">
        <div className="datatable-filters-wrapper"></div>

        <IconField>
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Clear Filters"
            outlined
            onClick={clearFilter}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="100"
            height="100"
            viewBox="0 0 50 50"
          >
            <path d="M 21 3 C 11.622998 3 4 10.623005 4 20 C 4 29.376995 11.622998 37 21 37 C 24.712383 37 28.139151 35.791079 30.9375 33.765625 L 44.085938 46.914062 L 46.914062 44.085938 L 33.886719 31.058594 C 36.443536 28.083 38 24.223631 38 20 C 38 10.623005 30.377002 3 21 3 z M 21 5 C 29.296122 5 36 11.703883 36 20 C 36 28.296117 29.296122 35 21 35 C 12.703878 35 6 28.296117 6 20 C 6 11.703883 12.703878 5 21 5 z"></path>
          </svg>
          <InputText
            value={globalFilterValue || ""}
            onChange={onGlobalFilterChange}
            placeholder="Search by keyword"
            disabled={!isUnlocked}
          />
        </IconField>
      </div>
    );
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <span>{option.name}</span>
      </div>
    );
  };

  const transactionTypeRowFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={transactionTypes}
        itemTemplate={itemTemplate}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="name"
        placeholder="Any"
        className="p-column-filter"
        maxSelectedLabels={1}
      />
    );
  };

  const taxTypeRowFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={taxTypes}
        itemTemplate={itemTemplate}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="name"
        placeholder="Any"
        className="p-column-filter"
        maxSelectedLabels={1}
      />
    );
  };

  const allowExpansion = (rowData) => {
    return rowData.settlementTransactions.length > 0 || isMobileView;
  };

   const transactionTypeBodyTemplate = (rowData) => {
      const base = rowData.type;
      let extra = "";
      //if (base === "OPTION" || base === "DIVIDEND") {
        if(rowData.description){
          extra = `(${rowData.description})`;
        }
        if (rowData.optionType && rowData.optionSide && rowData.description) {
          extra = `(${rowData.optionType} ${rowData.optionSide}) - (${rowData.description})`;
        } else if (rowData.optionType && rowData.description) {
          extra = `(${rowData.optionType}) - (${rowData.description})`;
        }
         else if(rowData.description){
           extra = `(${rowData.description})`;
         }

        return (
          <div>
            <div>{base}</div>
            {extra && <small style={{ color: "#666" }}>{extra}</small>}
          </div>
        );
     // }

      return <div>{base}</div>;
    };

  const rowExpansionTemplate = (data) => {
    return !isMobileView ? (
      <div className="p-3">
        <h5>Settlement info</h5>
        <DataTable value={data.settlementTransactions}>
          <Column field="instrument_id" header="Ticker" sortable></Column>
          <Column field="type" header="Transaction Type" style={{ maxWidth: "15rem", minWidth: "15rem" }} sortable body={transactionTypeBodyTemplate} ></Column>
          <Column field="costBasis" header="Purchase Price" sortable></Column>
          <Column
            field="quantity"
            header="Purchase Quantity"
            sortable
          ></Column>
          <Column field="date" header="Purchase Date" sortable></Column>
        </DataTable>
      </div>
    ) : (
      <div className="mobile-expand-info">
        <div className="expand-row">
          <h4>Account Name:</h4>
          <p>{data.accountName}</p>
        </div>
        <div className="expand-row">
          <h4>Ticker:</h4>
          <p>{data.tickerSymbol}</p>
        </div>
        <div className="expand-row">
          <h4>Transaction Type:</h4>
          <p>{data.type}</p>
        </div>

        <div className="expand-row">
          <h4>Purchase Date:</h4>
          <p>{data.purchaseDate}</p>
        </div>

        <div className="expand-row">
          <h4>Quantity:</h4>
          <p>{data.quantity}</p>
        </div>

        <div className="expand-row">
          <h4>Purchase Price:</h4>
          <p>{data.price}</p>
        </div>

        <div className="expand-row">
          <h4>Current Price:</h4>
          <p>{data.currentPrice}</p>
        </div>

        <div className="expand-row">
          <h4>Potential Loss:</h4>
          <p>{data.unrealizedProfitLoss}</p>
        </div>
        <div className="expand-row">
          <h4>Tax Type:</h4>
          <p>{data.taxType}</p>
        </div>
        <div className="expand-row">
          <h4>Tax Savings:</h4>
          <p>{data.taxesOwed}</p>
        </div>
        <div className="expand-row">
          <h4>Alternate Recommendations:</h4>
          <p>{formatTop3Recommendations(data.peerRecommendations)}</p>
        </div>
        <div className="expand-row">
          <h4>Description:</h4>
          <p>{data.description}</p>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="capital-summary-container">
        <CapitalSummaryCard
          title="Net Gains"
          value={netGains}
          type={netGains >= 0 ? 'positive' : 'negative'}
          tooltip="Net result of all realized gains (short-term + long-term) after losses."
        />
        <CapitalSummaryCard
          title="Current Taxes You Owe"
          value={currentTaxesOwed}
          type={currentTaxesOwed >= 0 ? 'negative' : 'positive'}
          tooltip="This is an estimate of the taxes you currently owe on your realized investment gains, based on your current tax rates."
        />
        <CapitalSummaryCard
          title="Potential Losses To Offset Gains&Income"
          value={Math.abs(potentialUnrealizedLosses)}
          type="neutral"
          tooltip="Total amount of unrealized losses that could be harvested to offset capital gains"
        />
      </div>

      <div className="summary-container">
        <div className="tax-savings-metrics">
          <h2 className="metrics-title">Tax Savings Analysis</h2>
          <div className="metrics-container">
            <div className="metric-item">
              <div className="metric-title">Capital Gains Offset</div>
              <div className="metric-value">${capitalGainsOffset.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="metric-description">
                Potential capital gains that can be offset through tax-loss harvesting
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-title">Income Deduction</div>
              <div className="metric-value">${incomeDeduction.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="metric-description">
                Additional losses that can be deducted from your taxable income (max $3,000)
              </div>
            </div>
            <div className="metric-item metric-item-highlight">
              <div className="metric-title">Estimated Tax Savings</div>
              <div className="metric-value">${totalTaxSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="metric-description">
                Total potential tax savings based on your {shortTermTaxRate}% tax rate
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {/* Unlock Button - Outside blurred area */}
        {!isUnlocked && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '30px',
            marginBottom: '30px',
            paddingTop: '20px',
            paddingBottom: '20px',
            zIndex: 10,
            position: 'relative'
          }}>
            <Button
              type="button"
              severity="success"
              onClick={handleUnlockClick}
              style={{
                background: 'linear-gradient(135deg, #2A9D8F 0%, #264653 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '700',
                boxShadow: '0 4px 15px rgba(42, 157, 143, 0.25)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FontAwesomeIcon icon={faLock} />
              Unlock Details
            </Button>
          </div>
        )}
        
        <Tooltip
          target=".remaining-quantity-tooltip"
          position="top"
          showDelay={100}
          hideDelay={100}
        />
        <Tooltip
          target=".tax-type-tooltip"
          position="top"
          showDelay={100}
          hideDelay={100}
        />
        <DataTable
          value={tableData}
          removableSort
          sortMode="multiple"
          paginator
          rows={10}
          dataKey="id"
          filters={filters}
          loading={loading}
          ref={tableRef}
          selection={selectedLossOpportunities}
          onSelectionChange={(e) => setSelectedLossOpportunities(e.value)}
          selectionMode="multiple"
          multiSortMeta={[
            {
              field: "profitLossWithoutPrefix",
              order: "1",
            },
          ]}
          globalFilterFields={[
            "accountName",
            "tickerSymbol",
            "type",
            "purchaseDate",
            "date",
            "taxType",
          ]}
          header={renderHeader()}
          emptyMessage="No transactions found."
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
        onValueChange={(e) => {
          getSummaryCardsValue(e);
        }}
        style={{ 
          filter: !isUnlocked ? 'blur(8px)' : 'none',
          pointerEvents: !isUnlocked ? 'none' : 'auto'
        }}
      >
        <Column
          selectionMode="multiple"
          style={{ width: "3rem" }}
        />
        <Column
          expander={allowExpansion}
          style={{ width: "3rem" }}
          className={isMobileView ? "mobile-row-expander" : ""}
        />
        {!isMobileView ? (
          <Column field="accountName" header="Account Name" sortable></Column>
        ) : null}
        <Column
          field="tickerSymbol"
          header="Ticker"
          sortable
          body={renderTickerCell}
          className="table-highlight-column-cell"
        ></Column>
        {!isMobileView ? (
          <Column
            field={"type"}
            header="Transaction Type"
            sortable
            showFilterMenu={true}
            style={{ maxWidth: "10rem", minWidth: "10rem" }}
            filter
            filterField="type"
            showFilterMatchModes={false}
            filterElement={transactionTypeRowFilterTemplate}
            filterMenuStyle={{ width: "14rem" }}
            body={transactionTypeBodyTemplate}
          ></Column>
        ) : null}
        {!isMobileView ? (
          <Column field="purchaseDate" header="Purchase Date" sortable></Column>
        ) : null}
        {!isMobileView ? (
          <Column 
            field="price" 
            header="Purchase Price" 
            sortable
            sortField="priceValue"
          ></Column>
        ) : null}
        {!isMobileView ? (
          <Column
            field="currentPriceValue"
            header="Current Price"
            body={renderCurrentPriceCell}
            sortable
            sortField="currentPriceValue"
          ></Column>
        ) : null}
        <Column 
          field="unrealizedProfitLoss" 
          header="Potential Loss" 
          sortable
          sortField="profitLossWithoutPrefix"
        ></Column>
        <Column
          field="taxesOwedValue"
          header="Tax Savings"
          body={renderTaxSavingsCell}
          sortable
          sortField="taxesOwedValue"
          className="table-highlight-column-cell"
        ></Column>
        <Column
          field="peerRecommendations"
          header="Alternate Recommendations"
          body={(rowData) =>
            formatTop3Recommendations(rowData.peerRecommendations)
          }
          className="table-highlight-column-cell"
        ></Column>
      </DataTable>
      
      {/* Execute Button */}
      {selectedLossOpportunities.length > 0 && (
        <div className="execute-button-container" style={{ marginTop: '20px', padding: '16px 20px', background: '#f8f9fa', borderTop: '2px solid #e9ecef', display: 'flex', justifyContent: 'center', borderRadius: '0 0 12px 12px' }}>
          {username === "demo" ? (
            <Button
              label={`Execute (${selectedLossOpportunities.length} lot${selectedLossOpportunities.length > 1 ? 's' : ''} selected)`}
              icon="pi pi-check-circle"
              onClick={handleExecuteSelectedLots}
              className="p-button-success calculate-button-primary execute-button"
              iconPos="left"
            />
          ) : (
            <Button
              label="Coming Soon"
              icon="pi pi-clock"
              disabled
              className="p-button-secondary execute-button-disabled"
              iconPos="left"
            />
          )}
        </div>
      )}
      </div>
      
      {/* Unlock Modal */}
      <DashboardUnlock
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        estimatedSavings={totalTaxSavings}
        onSuccess={handleUnlockSuccess}
        username={username}
      />
      
      {/* Execute Confirmation Dialog */}
      <Dialog
        header="Confirm Tax Loss Harvesting Execution"
        visible={showExecuteConfirm}
        onHide={() => {
          setShowExecuteConfirm(false);
          setReplacementSelections({});
        }}
        style={{ width: '600px', maxWidth: '90vw' }}
        className="execute-confirm-dialog"
        footer={
          <div className="execute-confirm-footer">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowExecuteConfirm(false);
                setReplacementSelections({});
              }}
              className="p-button-text p-button-secondary"
            />
            <Button
              label="Execute"
              icon="pi pi-check"
              onClick={confirmExecute}
              className="p-button-success calculate-button-primary"
      />
    </div>
        }
      >
        <div className="execute-confirm-content">
          <p className="execute-confirm-question">
            Execute {selectedLossOpportunities.length} selected lot{selectedLossOpportunities.length > 1 ? 's' : ''}?
          </p>
          <div className="replacement-selections-container" style={{ marginTop: '20px' }}>
            {selectedLossOpportunities.map((lot) => {
              const recommendations = lot.peerRecommendations;
              const hasRecommendations = recommendations && (
                Array.isArray(recommendations) ? recommendations.length > 0 : 
                typeof recommendations === 'string' ? recommendations.trim().length > 0 : false
              );
              const lotId = lot.id;
              const replacement = replacementSelections[lotId] || { buyReplacement: false, replacementTicker: '' };
              
              return (
                <div key={lotId} className="replacement-selection-item" style={{ 
                  padding: '16px', 
                  marginBottom: '12px', 
                  background: '#f8f9fa', 
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ marginBottom: '12px', fontWeight: '600', color: '#264653' }}>
                    {lot.tickerSymbol} - {formatCurrencyValue(Math.abs(lot.profitLossWithoutPrefix || 0))} loss
                  </div>
                  {hasRecommendations && (
                    <>
                      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox
                          inputId={`buy-replacement-${lotId}`}
                          checked={replacement.buyReplacement}
                          onChange={(e) => {
                            setReplacementSelections(prev => ({
                              ...prev,
                              [lotId]: {
                                ...prev[lotId],
                                buyReplacement: e.checked,
                                replacementTicker: e.checked ? (Array.isArray(recommendations) ? recommendations[0] : recommendations.split(/[\s,]+/)[0]) : ''
                              }
                            }));
                          }}
                        />
                        <label htmlFor={`buy-replacement-${lotId}`} style={{ cursor: 'pointer', fontWeight: '500' }}>
                          Buy replacement security
                        </label>
                      </div>
                      {replacement.buyReplacement && (
                        <div style={{ marginLeft: '28px', marginTop: '8px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', color: '#6c757d' }}>
                            Replacement ticker:
                          </label>
                          <InputText
                            value={replacement.replacementTicker || ''}
                            onChange={(e) => {
                              setReplacementSelections(prev => ({
                                ...prev,
                                [lotId]: {
                                  ...prev[lotId],
                                  replacementTicker: e.target.value.toUpperCase()
                                }
                              }));
                            }}
                            placeholder={Array.isArray(recommendations) ? recommendations[0] : recommendations.split(/[\s,]+/)[0]}
                            style={{ width: '100%', padding: '8px' }}
                          />
                          {recommendations && (
                            <div style={{ marginTop: '4px', fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
                              Suggested: {Array.isArray(recommendations) ? recommendations.slice(0, 3).join(', ') : recommendations.split(/[\s,]+/).slice(0, 3).join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Dialog>
      
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default TaxLossOpportunitiesFrame;